import { AnchorButton, Button, ButtonGroup, Classes, Colors } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import * as React from 'react'
import { Link } from 'react-router-dom'
import CX from 'classnames'
import config from '../../../config'
import FulfillmentStatusTag from '../../../routes/orders/components/tags/FulfillmentStatusTag'
import PaymentStatusTag from '../../../routes/orders/components/tags/PaymentStatusTag'
import { PickingAndPackStatusTag } from '../../../routes/orders/components/tags/PickingAndPackStatusTag'
import { formatMoneyWithCurrency } from '../../../utils'
import { usePermissions } from '../../../hooks/usePermissions'
import { pluralizeFromNumber } from '../../../utils/string'
import InteractiveDatetime from '../../InteractiveDatetime'
import { OrderDetailsGridSection } from './OrderDetailsGridSection'
import OrderStatusTag, { ValidOrderStatus } from '../../../routes/orders/components/tags/StatusTag'
import EmptyPlaceholder from '../../EmptyPlaceholder'
import './orderDetailsStyle.scss'

const OrderLink = ({ data }) => {
  const { permissions } = usePermissions()

  return permissions.includes('orders.all') ? (
    <Link className="order-link" to={`/orders/${data.id}`}>
      {data.id}
    </Link>
  ) : (
    data.id
  )
}

export const OrderDetailsTable = ({ orders, variants, productCarrierName, productsOrderItemPaidMap }) => {
  const pageSize = 12

  const [page, setPage] = React.useState(0)
  const currentPageStart = page * pageSize + 1
  const currentPageEnd = Math.min((page + 1) * pageSize, orders.length)
  const maxPages = React.useMemo(() => {
    // Round up so users can see the last page
    return Math.ceil((orders.length * 1.0) / pageSize) || 0
  }, [orders.length, pageSize])

  const pageOfOrders = orders.slice(page * pageSize, page * pageSize + pageSize)

  const [expandedRows, setExpandedRows] = React.useState(orders.length > 2 ? {} : { 0: true, 1: true })

  const allExpanded = React.useMemo(() => {
    return (
      Object.keys(expandedRows).length >= pageOfOrders.length && Object.values(expandedRows).every(val => val !== false)
    )
  }, [expandedRows, pageOfOrders.length])

  const toggleAllExpanded = React.useCallback(() => {
    if (Object.values(expandedRows).every(val => val === false)) {
      const allExpandedObj = {}
      // row expansion is handled per page, instead of per record
      pageOfOrders.slice(0, pageSize).forEach((order, i) => {
        allExpandedObj[i] = true
      })
      setExpandedRows(allExpandedObj)
    } else {
      setExpandedRows({})
    }
  }, [expandedRows, pageOfOrders])

  return (
    <>
      <table className="expander-table">
        <thead className={orders.length > 2 ? 'show-header-border' : ''}>
          <th className="expand-button-column">
            {orders.length > 2 && (
              <AnchorButton
                icon={allExpanded ? IconNames.CHEVRON_DOWN : IconNames.CHEVRON_RIGHT}
                onClick={toggleAllExpanded}
                minimal
              />
            )}
          </th>
          <th>Upright ID</th>
          <th>Payment Status</th>
          <th>Fulfillment Status</th>
          <th>Sold For</th>
        </thead>
        <tbody>
          {pageOfOrders.map((order, i) => {
            const orderItemSum = productsOrderItemPaidMap[order.id]
            const orderItemSumLabel = orderItemSum && formatMoneyWithCurrency(orderItemSum, order.currencyCode)

            let orderStatus: ValidOrderStatus = order.archivedAt ? 'archived' : 'open'
            if (order.cancelledAt) orderStatus = 'cancelled'

            let trackerHeader = 'Tracking Number'
            const trackingNumbers = order.shipmentTrackingCodes
            if (trackingNumbers && trackingNumbers.length > 1) {
              trackerHeader = `${trackerHeader} (1 of  ${trackingNumbers.length})`
            }

            const orderProductIds = order?.orderItems?.map(oi => oi.productId) || []
            let variantNames = []
            let variantNamesLabel = null
            if (variants?.length > 0) {
              variantNames = variants
                .map(variant => {
                  if (orderProductIds.includes(variant.id)) return variant.title
                  return null
                })
                .filter(variant => variant !== null)
              variantNamesLabel = variantNames.join(', ')
            }

            return (
              <tr key={order.id} className={expandedRows[i] ? 'expanded' : 'collapsed'}>
                <td className="expand-button-column">
                  <div>
                    <AnchorButton
                      style={{ userSelect: 'none', color: Colors.GRAY1 }}
                      icon={expandedRows[i] ? IconNames.CHEVRON_DOWN : IconNames.CHEVRON_RIGHT}
                      onClick={() =>
                        setExpandedRows(currRows => {
                          const nextRows = { ...currRows }
                          nextRows[i] = !currRows[i]
                          return nextRows
                        })
                      }
                      minimal
                      small
                    />
                  </div>
                </td>
                <td className={CX('data-column')}>
                  <div className={expandedRows[i] ? 'expanded' : 'collapsed'}>
                    <div className="drawer-order-link-container">
                      <OrderLink data={order} />
                    </div>
                    {expandedRows[i] && (
                      <div className="expanded-section">
                        <OrderDetailsGridSection
                          title="Channel"
                          contents={
                            order.marketAccount
                              ? order.marketAccount.fullHumanReadableName
                              : order.market.humanReadableName
                          }
                        />
                        <OrderDetailsGridSection
                          title="Order status"
                          contents={<OrderStatusTag value={orderStatus} />}
                        />
                      </div>
                    )}
                  </div>
                </td>
                <td className={CX('data-column')}>
                  <div className={expandedRows[i] ? 'expanded' : 'collapsed'}>
                    <div>
                      <PaymentStatusTag value={order.paymentStatus} />
                    </div>
                    {expandedRows[i] && (
                      <div className="expanded-section">
                        <OrderDetailsGridSection title="Channel Order ID" contents={order.externalOrderId} />
                        <OrderDetailsGridSection title="Shipping Method" contents={productCarrierName} />
                      </div>
                    )}
                  </div>
                </td>
                <td className={CX('data-column')}>
                  <div className={expandedRows[i] ? 'expanded' : 'collapsed'}>
                    <div className="fulfillment-tag-container">
                      <div>
                        <FulfillmentStatusTag value={order.fulfillmentStatus} shipmentsCount={order.shipmentsCount} />
                      </div>
                      {config.PICK_LISTS() && (
                        <div className="pick-and-pack-status-tag-container">
                          <PickingAndPackStatusTag
                            lockedForPickingUntil={order.lockedForPickingUntil}
                            lockedByUser={order.lockedByUser}
                            pickAndPackStatus={order.pickAndPackStatus}
                            fulfillmentStatus={order.fulfillmentStatus}
                            packingSlipPrintedAt={order.packingSlipPrintedAt}
                          />
                        </div>
                      )}
                    </div>
                    {expandedRows[i] && (
                      <div className="expanded-section">
                        <OrderDetailsGridSection
                          title="Paid at"
                          contents={order.paidAt && <InteractiveDatetime value={order.paidAt} />}
                        />
                        <OrderDetailsGridSection
                          title={trackerHeader}
                          contents={
                            trackingNumbers && (
                              <div className={Classes.TEXT_OVERFLOW_ELLIPSIS}>
                                <EmptyPlaceholder>{trackingNumbers[0]}</EmptyPlaceholder>
                              </div>
                            )
                          }
                        />
                      </div>
                    )}
                  </div>
                </td>
                <td className={CX('data-column')}>
                  <div className={expandedRows[i] ? 'expanded' : 'collapsed'}>
                    {orderItemSumLabel}
                    {expandedRows[i] && (
                      <div className="expanded-section">
                        <OrderDetailsGridSection
                          title="Order Total"
                          contents={formatMoneyWithCurrency(order.total, order.currencyCode)}
                        />
                        {variantNames.length > 0 && (
                          <OrderDetailsGridSection
                            title={`Variant${pluralizeFromNumber(variantNames.length)}`}
                            contents={
                              <div className="variant-label" title={variantNamesLabel}>
                                {variantNamesLabel}
                              </div>
                            }
                          />
                        )}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {maxPages > 1 && (
        <ButtonGroup className="pagination-container">
          <Button
            onClick={() => {
              setPage(page - 1)
              setExpandedRows({})
            }}
            disabled={page === 0}
          >
            Previous
          </Button>
          <div className="pagination-info">{`${currentPageStart} - ${currentPageEnd} of ${
            orders.length
          } Order${pluralizeFromNumber(orders.length)}`}</div>
          <Button
            onClick={() => {
              setPage(page + 1)
              setExpandedRows({})
            }}
            disabled={page >= maxPages - 1}
          >
            Next
          </Button>
        </ButtonGroup>
      )}
    </>
  )
}
